import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import axiosInstance from '../apiServices/axios';
import { WORKSPACE_PULSE_ACCESS } from '../apiServices/api_routes';

const ACCESS_CHECK_TIMEOUT_MS = 15000;

export function useWorkspacePulseAccess(workspaceId) {
    const { access, userId, workspacesLoading } = useSelector(state => ({
        access: state.user?.access,
        userId: state.user?.user?.id,
        workspacesLoading: Boolean(state.user?.workspacesLoading),
    }));

    const authReady = Boolean(userId && access);
    const canFetch = Boolean(workspaceId && authReady && !workspacesLoading);

    const [accessState, setAccessState] = useState({
        canView: false,
        canManageRecipients: false,
        loading: Boolean(workspaceId),
        error: null,
    });

    useEffect(() => {
        if (!workspaceId) {
            setAccessState({ canView: false, canManageRecipients: false, loading: false, error: null });
            return undefined;
        }

        if (!canFetch) {
            setAccessState(prev => ({ ...prev, loading: true, error: null }));
            return undefined;
        }

        let cancelled = false;
        const controller = new AbortController();
        const timeoutId = window.setTimeout(() => controller.abort(), ACCESS_CHECK_TIMEOUT_MS);

        setAccessState(prev => ({ ...prev, loading: true, error: null }));

        axiosInstance
            .get(WORKSPACE_PULSE_ACCESS, {
                params: { workspace_id: workspaceId },
                signal: controller.signal,
            })
            .then(({ data }) => {
                if (cancelled) return;
                setAccessState({
                    canView: Boolean(data?.can_view),
                    canManageRecipients: Boolean(data?.can_manage_recipients),
                    loading: false,
                    error: null,
                });
            })
            .catch(error => {
                if (cancelled) return;
                const timedOut = error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
                setAccessState({
                    canView: false,
                    canManageRecipients: false,
                    loading: false,
                    error: timedOut ? 'timeout' : 'failed',
                });
            })
            .finally(() => {
                window.clearTimeout(timeoutId);
            });

        return () => {
            cancelled = true;
            controller.abort();
            window.clearTimeout(timeoutId);
        };
    }, [workspaceId, canFetch]);

    return {
        canView: accessState.canView,
        canManageRecipients: accessState.canManageRecipients,
        loading: accessState.loading,
        error: accessState.error,
        authReady,
    };
}
